// Lightweight tRPC v11 fetch helpers для _api.ts модулей фронтенда.
//
// Используется в `components/{notes,flashcards,quizzes,weak-spots,tutor}/_api.ts`
// — там, где исторически были fetch на MSW REST-роуты. Хук-сигнатуры
// (useNotesList, useFlashcardSets и т.д.) остаются прежними, чтобы не
// переписывать 50+ UI-компонентов.
//
// Альтернатива (типизированная) — `api.{module}.{procedure}.useQuery()` из
// lib/trpc-client.ts. Используется во вновь создаваемых модулях / новых
// процедурах. Для legacy _api.ts оставляем raw fetch — пользы от
// двойной миграции (REST → tRPC URL → tRPC react hook) нет, инвариант
// (типы у backend и frontend) держится через packages/shared/contracts.
//
// 401 interceptor общий с tRPC client — открывает AuthExpiredModal через
// useAuthExpiredStore (см. lib/trpc-client.ts).

import { notifySessionExpiredFrom401 } from "./auth-expired-store";
import { getAccessToken } from "./auth-storage";

// API base: абсолютный VITE_API_URL (Capacitor) либо same-origin "" (web). См. auth-client.ts.
const TRPC_BASE = `${import.meta.env.VITE_API_URL ?? ""}/api/trpc`;

const AUTH_EXCLUDE_PATTERNS = [
	"auth.login",
	"auth.refresh",
	"auth.consent",
	"auth.logout",
	"auth.logoutAll",
];

function shouldTriggerAuthExpired(procedure: string): boolean {
	return !AUTH_EXCLUDE_PATTERNS.some((p) => procedure.startsWith(p));
}

function authHeaders(): Record<string, string> {
	const token = getAccessToken();
	const headers: Record<string, string> = {
		"content-type": "application/json",
	};
	if (token) headers.authorization = `Bearer ${token}`;
	return headers;
}

function handleAuthError(procedure: string, status: number): void {
	if (status === 401 && shouldTriggerAuthExpired(procedure)) {
		try {
			notifySessionExpiredFrom401();
		} catch {
			// store не инициализирован — игнор.
		}
	}
}

interface TrpcResponse<T> {
	result?: { data?: T };
	error?: { message?: string; data?: { code?: string } };
}

/**
 * tRPC v11 query call (GET с input в URL query).
 *
 * @example
 * const data = await trpcQuery<{ items: Note[] }>('notes.list', { limit: 20 });
 */
export async function trpcQuery<T>(procedure: string, input?: unknown): Promise<T> {
	const url = new URL(`${TRPC_BASE}/${procedure}`, window.location.origin);
	if (input !== undefined) {
		url.searchParams.set("input", JSON.stringify(input));
	}
	const res = await fetch(url.toString(), {
		credentials: "include",
		headers: authHeaders(),
	});
	if (!res.ok) {
		handleAuthError(procedure, res.status);
		const errText = await res.text().catch(() => "");
		throw new Error(`tRPC ${procedure} failed (${res.status}): ${errText}`);
	}
	const json = (await res.json()) as TrpcResponse<T>;
	if (json.error) {
		throw new Error(json.error.message ?? `tRPC ${procedure} error`);
	}
	if (!json.result || json.result.data === undefined) {
		throw new Error(`tRPC ${procedure}: empty result`);
	}
	return json.result.data;
}

/**
 * tRPC v11 mutation call (POST с input в body).
 *
 * @example
 * const { id } = await trpcMutation<{ id: string }>('notes.create', { title, materialId });
 */
export async function trpcMutation<T>(procedure: string, input?: unknown): Promise<T> {
	const url = `${TRPC_BASE}/${procedure}`;
	const res = await fetch(url, {
		method: "POST",
		credentials: "include",
		headers: authHeaders(),
		body: JSON.stringify(input ?? {}),
	});
	if (!res.ok) {
		handleAuthError(procedure, res.status);
		const errText = await res.text().catch(() => "");
		throw new Error(`tRPC ${procedure} failed (${res.status}): ${errText}`);
	}
	const json = (await res.json()) as TrpcResponse<T>;
	if (json.error) {
		throw new Error(json.error.message ?? `tRPC ${procedure} error`);
	}
	if (!json.result || json.result.data === undefined) {
		throw new Error(`tRPC ${procedure}: empty result`);
	}
	return json.result.data;
}
